import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Columns, Row } from '@app/components/simple-table/simple-table.component';
import { Employee } from '@app/models/employee/employee.model';
import { TimeOffType } from '@app/models/time-off-v3/time-off-type.model';
import { WorkSchedule } from '@app/models/time-off-v3/works-schedule.model';
import { NotifyService } from '@app/services';
import { BulkSelectEmployeeDialogComponent } from '@app/components';

const mapEmployeesToRow = (employee: Employee): Row => {
    return {
        cells: [{ employee, isAvatar: true }],
    };
};

@Component({
    selector: 'app-work-schedules-enrolled-employees',
    templateUrl: './work-schedules-enrolled-employees.template.html',
    styleUrls: ['./work-schedules-enrolled-employees.style.scss'],
})
export class WorkSchedulesEnrolledEmployeesComponent implements OnInit {
    @ViewChild('appEmployeeSelection', { static: true }) appEmployeeSelection: BulkSelectEmployeeDialogComponent;
    @Input() workSchedule?: WorkSchedule;

    isLoading = false;
    assignedEmployees: Employee[] = [];
    columns: Columns[] = [
        { columnName: 'time-off-v3.policyComponents.enrolled-employees.nameColumn', columnSpan: 'columns-4' },
    ];

    rows: Row[] = [];
    timeOffTypes: TimeOffType[] = [];

    constructor(
        private notify: NotifyService,
        private route: ActivatedRoute
    ) {}

    ngOnInit(): void {
        this.loadData();
    }

    private async loadData(): Promise<void> {
        try {
            this.isLoading = true;
            const workSchedule = await WorkSchedule.with('employees').find(this.route.snapshot.params['work-schedule']);

            this.workSchedule = workSchedule;

            this.assignedEmployees = workSchedule.employees.filter(({ status }) => status !== 'terminated');

            this.rows = this.assignedEmployees.map(mapEmployeesToRow);
        } catch (err) {
            this.notify.error(err);
        } finally {
            this.isLoading = false;
        }
    }

    onSearch(searchQuery: string | null): void {
        if (searchQuery === null) {
            this.rows = this.assignedEmployees.map(mapEmployeesToRow);
            return;
        }

        const nameIncludesSearchQuery = (row: Row): boolean =>
            (row.cells[0] as { employee: Employee; isAvatar: boolean }).employee.fullName
                .toLowerCase()
                .includes(searchQuery.toLocaleLowerCase());

        this.rows = this.assignedEmployees.map(mapEmployeesToRow).filter(nameIncludesSearchQuery);
    }

    async showAssignDialog(): Promise<void> {
        const workSchedule = this.workSchedule as WorkSchedule;

        const currentlyAttachedEmployeeIds = workSchedule.employees.map((employee) => employee.id);

        const employeeIdsFromMassAssignment: number[] = (
            await this.appEmployeeSelection.show(workSchedule.employees)
        ).map((employee: Employee) => employee.id);

        const attachedEmployeesIds: number[] = employeeIdsFromMassAssignment.filter(
            (id) => !currentlyAttachedEmployeeIds.includes(id)
        );

        const detachedEmployeesIds: number[] = currentlyAttachedEmployeeIds.filter(
            (id) => !employeeIdsFromMassAssignment.includes(id)
        );

        await this.workSchedule
            ?.attach('employees', attachedEmployeesIds)
            .detach('employees', detachedEmployeesIds)
            .save();

        this.loadData();
    }
}
